/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useContext, useMemo } from 'react';

import { elasticApi, isBrowser } from '@lucid/utils';
import { useURLParams } from '@lucid/hooks';
import debounce from 'lodash/debounce';
import { LayoutContext } from '@lucid/components';

import type { Lucid } from 'types/types';
import type { CollectionCard } from '../base-templates/collection-base';

interface UseCaseStudySearchProps {
  /** Starting point for pagination params */
  initialPage?: number;
  /** Card data to filter on Search */
  collectionCards: CollectionCard[];
  /** Sets state to Cards meeting search criteria */
  setCards: (value: React.SetStateAction<CollectionCard[]>) => void;
  /** The current pagination page */
  page: number;
  /** Sets the current pagination page */
  setPage: (value: React.SetStateAction<number>) => void;
  /** Ref to the search bar input element */
  searchBarRef: React.RefObject<HTMLInputElement>;
  /** The current industry filter value */
  collectionIndustry?: string;
  /** The current team filter value */
  collectionTeam?: string;
}

const useCaseStudySearch = ({
  initialPage = 1,
  collectionCards,
  setCards,
  page,
  setPage,
  searchBarRef,
  collectionIndustry,
  collectionTeam,
}: UseCaseStudySearchProps) => {
  const { paramValue, updateURLParam } = useURLParams('s');
  const [searchQuery, setSearchQuery] = useState(paramValue);
  const [searchResults, setSearchResults] = useState<string[]>([]);
  const [isQueryingElastic, setIsQueryingElastic] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const { shortCode } = useContext(LayoutContext);

  const updateSearchResults = useMemo(
    () =>
      debounce((value: string) => {
        updateURLParam(value);
        setIsQueryingElastic(true);
        setSearchQuery(value);
        setPage(1);
      }, 800),
    [updateURLParam],
  );

  useEffect(() => {
    // set searchQuery value from 's' URL param on page load
    if (searchBarRef?.current?.value === paramValue) {
      setSearchQuery(paramValue);
    }
  }, [paramValue, searchBarRef]);

  useEffect(() => {
    // update page when URL pathname changes
    if (isBrowser() && !window.location.pathname.endsWith(`/${page}`) && page !== 1) {
      setPage(initialPage);
    }
  }, [page, initialPage, searchBarRef]);

  useEffect(() => {
    // update search bar value when 's' url param changes
    // this is necessary for on-page navigation which updates the URL but doesn't trigger a page reload
    if (
      isBrowser() &&
      searchBarRef &&
      searchBarRef.current &&
      !isLoading &&
      searchBarRef.current !== document.activeElement
    ) {
      searchBarRef.current.value = paramValue;
    }
  }, [isLoading, paramValue, searchBarRef]);

  // Query Elastic
  useEffect(() => {
    const fetchElasticResults = async () => {
      if (!searchQuery.trim()) {
        return setSearchResults([]);
      }

      try {
        const response = await elasticApi.search({
          engine: 'case-studies',
          languageKey: shortCode,
          query: searchQuery.trim(),
          size: 1000,
        });

        const elasticResults = response?.results.map(result => result.id.raw);
        setSearchResults(elasticResults ?? []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchElasticResults();
  }, [shortCode, searchQuery]);

  useEffect(() => {
    setIsQueryingElastic(false);
  }, [searchResults]);

  // Search
  useEffect(() => {
    if (isQueryingElastic) {
      return;
    }

    const searchFilters = [
      {
        key: 'industries',
        field: 'collection_industry_id',
        value: collectionIndustry,
      },
      {
        key: 'teams',
        field: 'collection_team_id',
        value: collectionTeam,
      },
    ] as const;

    const isFilterValueInCard = (card: CollectionCard, filter: (typeof searchFilters)[number]) => {
      if (!filter.value) {
        return true;
      }

      const filterFieldValues = (card[filter.key] || []) as Partial<{
        [K in typeof filter.field]:
          | Lucid.Directus.Generated.Collection_Industry
          | Lucid.Directus.Generated.Collection_Team
          | Lucid.Directus.Generated.Collection_Topic;
      }>[];

      return filterFieldValues.some(value => value[filter.field]?.name === filter.value);
    };

    const filteredCards = collectionCards?.filter(card => {
      if (searchQuery && card?.id && !searchResults.includes(card.id)) {
        return false;
      }

      return searchFilters.every(filter => isFilterValueInCard(card, filter));
    });

    if (searchQuery) {
      // searchResults returns in order of relevancy
      const sortedCards = filteredCards?.sort((a, b) => searchResults.indexOf(a.id!) - searchResults.indexOf(b.id!));

      return setCards(sortedCards);
    }

    setCards(filteredCards);
  }, [collectionCards, collectionIndustry, collectionTeam, isQueryingElastic, searchQuery, searchResults]);

  useEffect(() => {
    if (paramValue === searchQuery && !isQueryingElastic) {
      setIsLoading(false);
    }
  }, [isQueryingElastic, paramValue, searchQuery]);

  const handleSearch = (value: string) => {
    setIsLoading(true);
    updateSearchResults(value);
  };

  return {
    handleSearch,
    isLoading,
    searchQuery,
  };
};

export default useCaseStudySearch;
